import type { StoryPageVm } from "./storyPages.types";

/** How the Story SQL list is grouped. */
export type StorySqlGroupMode = "all" | "page" | "tag";

export interface StorySqlGroupable {
  id: string;
  /** The page the chart is on; absent or unknown is the first page. */
  pageId?: string | null;
  tags?: string[];
}

export interface StorySqlGroup<T> {
  key: string;
  /** Null for the single ungrouped list ("all"). */
  title: string | null;
  items: T[];
}

/**
 * The charts in groups. `all`: one untitled group. `page`: one group per page in page order, empty
 * pages left out. `tag`: one group per tag in first-seen order (a chart with two tags is in both),
 * then the untagged charts under `untaggedTitle`.
 */
export const groupStorySqlCharts = <T extends StorySqlGroupable>(
  charts: readonly T[],
  mode: StorySqlGroupMode,
  pages: readonly StoryPageVm[],
  untaggedTitle: string,
): StorySqlGroup<T>[] => {
  if (mode === "page" && pages.length > 0) {
    const known = new Set(pages.map((page) => page.id));
    const pageOf = (chart: T) => (known.has(chart.pageId ?? null) ? (chart.pageId ?? null) : pages[0].id);
    return pages
      .map((page) => ({
        key: `page:${page.id ?? ""}`,
        title: page.title,
        items: charts.filter((chart) => pageOf(chart) === page.id),
      }))
      .filter((group) => group.items.length > 0);
  }
  if (mode === "tag") {
    const byTag = new Map<string, T[]>();
    const untagged: T[] = [];
    charts.forEach((chart) => {
      const tags = chart.tags ?? [];
      if (tags.length === 0) untagged.push(chart);
      tags.forEach((tag) => byTag.set(tag, [...(byTag.get(tag) ?? []), chart]));
    });
    const groups: StorySqlGroup<T>[] = Array.from(byTag, ([tag, items]) => ({ key: `tag:${tag}`, title: tag, items }));
    if (untagged.length > 0) groups.push({ key: "tag:", title: untaggedTitle, items: untagged });
    return groups;
  }
  return [{ key: "all", title: null, items: [...charts] }];
};

/** Whether a grouping has anything to group: pages need two or more, tags at least one tagged chart. */
export const canGroupBy = (
  mode: StorySqlGroupMode,
  pages: readonly StoryPageVm[],
  charts: readonly StorySqlGroupable[],
): boolean => {
  if (mode === "page") return pages.length > 1;
  if (mode === "tag") return charts.some((chart) => (chart.tags ?? []).length > 0);
  return true;
};
